import { styles, useBreakpoints } from "@repo/helpers";
import { useRef } from "react";

import { MapConfigProvider } from "./config/mapConfigContext";
import { MapFiltersProvider } from "./filters/filterContext";
import { usePanels } from "./hooks/usePanels";
import { usePinnedBelowHost } from "./hooks/usePinnedBelowHost";
import { MapLayersProvider } from "./layers/layerContext";
import { LayeredMap, useLayeredMapData } from "./map";
import { MapToolbar } from "./mapToolbar";
import { PANEL_IDS } from "./panels";
import { RightSidebar } from "./rightSidebar";
import { MapSidebar } from "./sidebar";
import { SlidePanel } from "./slidePanel";

export function LayerMaps() {
	return (
		<MapConfigProvider>
			<MapLayersProvider>
				<MapFiltersProvider>
					<LayeredMapPage />
				</MapFiltersProvider>
			</MapLayersProvider>
		</MapConfigProvider>
	);
}

function LayeredMapPage() {
	const { openPanel, closingPanel, toggle, close, closeAll, finishClosing } = usePanels();
	const { isDesktopUp } = useBreakpoints();
	const mapData = useLayeredMapData();
	const mapCovered = !isDesktopUp && openPanel !== null;

	const anchorRef = useRef<HTMLDivElement>(null);
	const mainRef = useRef<HTMLElement>(null);
	usePinnedBelowHost(anchorRef, mainRef, !isDesktopUp);

	return (
		<>
			{/* From 1000px the host shows its desktop menu, whose floated items spill below the header once they wrap. */}
			<div ref={anchorRef} className="min-[1000px]:clear-both" />
			<main
				ref={mainRef}
				className="fixed inset-x-0 bottom-0 top-0 z-[1] flex flex-col overflow-hidden lg:static lg:h-[87vh]"
			>
				<MapToolbar
					openPanel={openPanel}
					onTogglePanel={toggle}
					pinCount={mapData.markers.length}
					geoMiles={mapData.geoMiles}
					loading={mapData.loading}
				/>
				<div className="relative flex min-h-0 flex-1">
					<SlidePanel
						id={PANEL_IDS.layers}
						title="Maps & layers"
						side="left"
						open={openPanel === "layers"}
						closing={closingPanel === "layers"}
						pinned={isDesktopUp}
						onClose={() => close("layers")}
						onClosed={finishClosing}
						className={styles(
							"w-[min(22rem,88vw)]",
							"lg:visible lg:static lg:z-auto lg:mr-4 lg:w-[20%] lg:min-w-72 lg:translate-x-0 lg:shadow-none lg:transition-none",
						)}
						headerClassName="lg:hidden"
					>
						<div className="min-h-0 flex-1 px-3 pb-3 lg:p-0">
							<MapSidebar onExecute={() => close("layers")} />
						</div>
					</SlidePanel>
					<section {...(mapCovered ? { inert: "" } : {})} className="relative isolate h-full min-w-0 flex-1">
						<LayeredMap {...mapData} />
					</section>
					<RightSidebar
						open={openPanel === "search"}
						closing={closingPanel === "search"}
						onToggle={() => toggle("search")}
						onClose={() => close("search")}
						onClosed={finishClosing}
					/>
					<div
						aria-hidden
						onClick={closeAll}
						className={styles(
							"absolute inset-0 z-20 bg-black/40 transition-opacity duration-300 motion-reduce:transition-none lg:hidden",
							openPanel ? "opacity-100" : "pointer-events-none opacity-0",
						)}
					/>
				</div>
			</main>
		</>
	);
}
